import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Autocomplete,
  TextField,
  ToggleButtonGroup,
  ToggleButton,
  Button,
  Stack,
  Typography,
} from '@mui/material';
import {
  Search as SearchIcon,
  AccountTree as DescendantIcon,
  FamilyRestroom as AncestorIcon,
  HourglassEmpty as HourglassIcon,
  UnfoldMore as ExpandAllIcon,
  UnfoldLess as CollapseAllIcon,
  ZoomIn as ZoomInIcon,
  ZoomOut as ZoomOutIcon,
  CenterFocusStrong as FitToViewIcon,
  Download as DownloadIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  ArrowUpward as ArrowUpwardIcon,
  Home as HomeIcon,
} from '@mui/icons-material';
import { Person, PersonSearchResult, TreeViewMode, TreeLayoutAlgorithm, TreeExportFormat } from '../../types';
import { PersonService } from '../../services/personService';

/**
 * TreeControls Component
 *
 * Controls for tree navigation and configuration
 *
 * Features:
 * - Focus on Person search (autocomplete)
 * - View mode toggle (Descendant/Ancestor/All Relatives)
 * - Layout algorithm selector (Tree/Cluster/Radial)
 * - Zoom controls (In/Out/Fit)
 * - Expand All / Collapse All buttons
 * - Export button (SVG/PNG)
 *
 * @component
 */
interface TreeControlsProps {
  /** Callback when user selects a person to focus on */
  onFocusPerson: (personId: string) => void;

  /** Current view mode */
  viewMode: TreeViewMode;

  /** Callback when view mode changes */
  onViewModeChange: (mode: TreeViewMode) => void;

  /** Current focused person ID (optional) */
  currentPersonId?: string;

  /** Total persons in tree */
  totalPersons?: number;

  /** Total generations in tree */
  totalGenerations?: number;

  /** Callback to expand all nodes */
  onExpandAll?: () => void;

  /** Callback to collapse all nodes */
  onCollapseAll?: () => void;

  /** Callback to zoom in */
  onZoomIn?: () => void;

  /** Callback to zoom out */
  onZoomOut?: () => void;

  /** Callback to fit tree to view */
  onFitToView?: () => void;

  /** Callback to export tree */
  onExport?: (format: TreeExportFormat) => void;

  /** Current layout algorithm (for interactive view) */
  layoutAlgorithm?: TreeLayoutAlgorithm;

  /** Callback when layout algorithm changes */
  onLayoutAlgorithmChange?: (algorithm: TreeLayoutAlgorithm) => void;

  /** Is interactive view active */
  isInteractiveView?: boolean;

  /** Navigation callbacks */
  onNavigateBack?: () => void;
  onNavigateForward?: () => void;
  onNavigateToParent?: () => void;
  onNavigateToRoot?: () => void;

  /** Navigation state */
  canNavigateBack?: boolean;
  canNavigateForward?: boolean;
  hasParent?: boolean;
  navigationPosition?: string; // e.g., "3 of 7"
}

export const TreeControls: React.FC<TreeControlsProps> = ({
  onFocusPerson,
  viewMode,
  onViewModeChange,
  currentPersonId,
  totalPersons = 0,
  totalGenerations = 0,
  onExpandAll,
  onCollapseAll,
  onZoomIn,
  onZoomOut,
  onFitToView,
  onExport,
  layoutAlgorithm,
  onLayoutAlgorithmChange,
  isInteractiveView = false,
  onNavigateBack,
  onNavigateForward,
  onNavigateToParent,
  onNavigateToRoot,
  canNavigateBack = false,
  canNavigateForward = false,
  hasParent = false,
  navigationPosition,
}) => {
  // ===== State =====
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<PersonSearchResult[]>([]);
  const [selectedPerson, setSelectedPerson] = useState<PersonSearchResult | null>(null);
  const [loading, setLoading] = useState(false);

  // ===== Effects =====

  /**
   * Search for persons when query changes
   */
  useEffect(() => {
    const searchPersons = async () => {
      if (searchQuery.length < 2) {
        setSearchResults([]);
        return;
      }

      try {
        setLoading(true);
        const results = await PersonService.searchPersons(searchQuery);
        setSearchResults(results);
      } catch (error) {
        console.error('Failed to search persons:', error);
        setSearchResults([]);
      } finally {
        setLoading(false);
      }
    };

    const debounceTimer = setTimeout(searchPersons, 300);
    return () => clearTimeout(debounceTimer);
  }, [searchQuery]);

  // ===== Event Handlers =====

  /**
   * Handle person selection from autocomplete
   */
  const handlePersonSelect = (_event: any, person: PersonSearchResult | null) => {
    if (person) {
      setSelectedPerson(person);
      onFocusPerson(person.id);
    }
  };

  /**
   * Handle view mode toggle
   */
  const handleViewModeChange = (_event: React.MouseEvent<HTMLElement>, newMode: TreeViewMode | null) => {
    if (newMode !== null) {
      onViewModeChange(newMode);
    }
  };

  /**
   * Format person name for display
   */
  const getPersonLabel = (person: PersonSearchResult): string => {
    let label = `${person.firstName} ${person.lastName}`;
    if (person.birthDate) {
      const birthYear = new Date(person.birthDate).getFullYear();
      label += ` (${birthYear}`;
      if (person.deathDate) {
        const deathYear = new Date(person.deathDate).getFullYear();
        label += `-${deathYear}`;
      } else {
        label += '-present';
      }
      label += ')';
    }
    return label;
  };

  // ===== Render =====

  return (
    <Paper
      elevation={2}
      sx={{
        p: { xs: 2, sm: 3 },
        mb: 3,
        borderRadius: 2,
      }}
    >
      <Stack spacing={3}>
        {/* Top Row: Search and View Mode */}
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={2}
          sx={{ alignItems: { xs: 'stretch', md: 'center' } }}
        >
          {/* Person Search */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Autocomplete
              options={searchResults}
              getOptionLabel={getPersonLabel}
              loading={loading}
              value={selectedPerson}
              onChange={handlePersonSelect}
              onInputChange={(_event, value) => setSearchQuery(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Focus on Person"
                  placeholder="Search by name..."
                  variant="outlined"
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: <SearchIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  <Box>
                    <Typography variant="body2">{getPersonLabel(option)}</Typography>
                    {option.birthPlace && (
                      <Typography variant="caption" color="text.secondary">
                        {option.birthPlace}
                      </Typography>
                    )}
                  </Box>
                </li>
              )}
              noOptionsText={searchQuery.length < 2 ? 'Type at least 2 characters' : 'No people found'}
            />
          </Box>

          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={handleViewModeChange}
            size="small"
            sx={{ alignSelf: { xs: 'stretch', md: 'center' } }}
          >
            <ToggleButton value="descendant" aria-label="descendant view">
              <DescendantIcon sx={{ mr: { xs: 0, sm: 1 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Descendants
              </Box>
            </ToggleButton>
            <ToggleButton value="ancestor" aria-label="ancestor view">
              <AncestorIcon sx={{ mr: { xs: 0, sm: 1 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                Ancestors
              </Box>
            </ToggleButton>
            <ToggleButton value="hourglass" aria-label="all relatives view">
              <HourglassIcon sx={{ mr: { xs: 0, sm: 1 } }} />
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>
                All Relatives
              </Box>
            </ToggleButton>
          </ToggleButtonGroup>
        </Stack>

        {/* Navigation Controls */}
        {(onNavigateBack || onNavigateForward || onNavigateToParent || onNavigateToRoot) && (
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap' }}>
            {onNavigateBack && (
              <Button
                variant="outlined"
                size="small"
                disabled={!canNavigateBack}
                onClick={onNavigateBack}
                startIcon={<ArrowBackIcon />}
                sx={{ minWidth: 'auto' }}
                title="Navigate back"
              >
                Back
              </Button>
            )}
            {onNavigateForward && (
              <Button
                variant="outlined"
                size="small"
                disabled={!canNavigateForward}
                onClick={onNavigateForward}
                startIcon={<ArrowForwardIcon />}
                sx={{ minWidth: 'auto' }}
                title="Navigate forward"
              >
                Forward
              </Button>
            )}
            {onNavigateToParent && (
              <Button
                variant="outlined"
                size="small"
                disabled={!hasParent}
                onClick={onNavigateToParent}
                startIcon={<ArrowUpwardIcon />}
                sx={{ minWidth: 'auto' }}
                title="Navigate to parent"
              >
                Parent
              </Button>
            )}
            {onNavigateToRoot && (
              <Button
                variant="outlined"
                size="small"
                onClick={onNavigateToRoot}
                startIcon={<HomeIcon />}
                sx={{ minWidth: 'auto' }}
                title="Navigate to root ancestor"
              >
                Home
              </Button>
            )}
            {navigationPosition && (
              <Typography
                variant="caption"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  px: 1,
                  color: 'text.secondary',
                }}
              >
                {navigationPosition}
              </Typography>
            )}
          </Stack>
        )}

        {/* Bottom Row: Stats and Actions */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={2}
          sx={{
            alignItems: { xs: 'stretch', sm: 'center' },
            justifyContent: 'space-between',
          }}
        >
          {/* Tree Statistics */}
          <Box>
            <Typography variant="body2" color="text.secondary">
              <strong>{totalPersons}</strong> {totalPersons === 1 ? 'person' : 'people'} across{' '}
              <strong>{totalGenerations}</strong> {totalGenerations === 1 ? 'generation' : 'generations'}
            </Typography>
          </Box>

          {/* Action Buttons */}
          <Stack direction="row" spacing={1}>
            {/* Zoom Controls (Interactive View Only) */}
            {isInteractiveView && onZoomIn && (
              <Button
                variant="outlined"
                size="small"
                onClick={onZoomIn}
                sx={{ minWidth: 'auto', px: 1 }}
                title="Zoom In"
              >
                <ZoomInIcon />
              </Button>
            )}
            {isInteractiveView && onZoomOut && (
              <Button
                variant="outlined"
                size="small"
                onClick={onZoomOut}
                sx={{ minWidth: 'auto', px: 1 }}
                title="Zoom Out"
              >
                <ZoomOutIcon />
              </Button>
            )}
            {isInteractiveView && onFitToView && (
              <Button
                variant="outlined"
                size="small"
                onClick={onFitToView}
                sx={{ minWidth: 'auto', px: 1 }}
                title="Fit to View"
              >
                <FitToViewIcon />
              </Button>
            )}

            {/* Expand/Collapse Controls */}
            {onExpandAll && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<ExpandAllIcon />}
                onClick={onExpandAll}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Expand All
              </Button>
            )}
            {onCollapseAll && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CollapseAllIcon />}
                onClick={onCollapseAll}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Collapse All
              </Button>
            )}

            {/* Export Control (Interactive View Only) */}
            {isInteractiveView && onExport && (
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={() => onExport('png')}
                sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
              >
                Export
              </Button>
            )}
          </Stack>
        </Stack>
      </Stack>
    </Paper>
  );
};
